import { NoxusAlert } from "@/components/UI/NoxusAlert";
import React, {
  CSSProperties,
  Dispatch,
  createContext,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import type {
  DraggableAttributes,
  DraggableSyntheticListeners,
} from "@dnd-kit/core";
import {
  useReactTable,
  flexRender,
  getCoreRowModel,
  ColumnDef,
  SortingState,
  createColumnHelper,
  Row,
} from "@tanstack/react-table";
import { TableHeader, TableBody, TableHead, TableCell } from "../shadcn/table";
import { Checkbox } from "../shadcn/checkbox";
import { Skeleton } from "../shadcn/skeleton";
import { Separator } from "../shadcn/separator";
import { cn } from "@/lib/utils";
import { useFloating, offset, flip, shift } from "@floating-ui/react";

import {
  DonutIcon1,
  DonutIcon2,
  DonutIcon3,
  DonutIcon4,
  DonutIcon5,
} from "../Icons";

import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { SetStateAction } from "jotai";
import {
  restrictToVerticalAxis,
  restrictToParentElement,
} from "@dnd-kit/modifiers";
import ImageFallback from "../ImageWithFallback";
import { IModelExtended } from "../Form/ModelSelectField";
import {
  formatNumber,
  formatTime,
  capitalizeFirstLetter,
} from "@/utils/generic";
import { LlmModelProperties } from "@/generated";
import {
  ChevronDown,
  ChevronUp,
  GripVertical,
  ShieldCheck,
  Link2Off,
} from "lucide-react";

import { NoxusTooltip } from "../UI/NoxusTooltip";
import ImageWithFallback from "../ImageWithFallback";
import {
  ModelStatusBadge,
  type ModelStatus,
} from "@/components/Views/Models/ModelStatusBadge";

interface ModelHealthFields {
  lifecycle_state?: string;
  last_error?: string | null;
  consecutive_failures?: number;
  last_checked_at?: string | null;
}
import { Badge } from "@/components/shadcn/badge";
import {
  CapabilityChip,
  sortCapabilities,
} from "@/components/Views/Models/CapabilityBadge";
import { Portal } from "../UI/Portal";
import { RetirementNotice } from "../Views/Models/RetirementNotice";

const getDonutIcon = (
  value: number | string | undefined,
  limits: number[] = [60, 70, 78, 85]
) => {
  if (value == undefined) return <></>;

  const numberValue = Number(value);
  if (numberValue < limits[0]) {
    return <DonutIcon5 fontSize="1rem" />;
  } else if (numberValue >= limits[0] && numberValue < limits[1]) {
    return <DonutIcon4 fontSize="1rem" />;
  } else if (numberValue >= limits[1] && numberValue < limits[2]) {
    return <DonutIcon3 fontSize="1rem" />;
  } else if (numberValue >= limits[2] && numberValue < limits[3]) {
    return <DonutIcon2 fontSize="1rem" />;
  } else {
    return <DonutIcon1 fontSize="1rem" />;
  }
};

const getLocationImage = (
  location: string | undefined,
  isTooltip?: boolean
) => {
  if (!location) return <></>;
  let image;
  let place;

  switch (location) {
    case "europe":
      image =
        "https://storage.googleapis.com/image-storage-spot-manual/app/location_europe.png";
      place = "EU";
      break;
    default:
      image =
        "https://storage.googleapis.com/image-storage-spot-manual/app/location_us.png";
      place = "US";
  }

  return (
    <div className="flex items-center gap-2">
      <div
        className={cn("relative shrink-0", isTooltip ? "w-5 h-5" : "w-4 h-4")}
      >
        <ImageFallback fill src={image} alt={`Flag for ${place}`} />
      </div>
      {!isTooltip && <span className="text-xs">{place}</span>}
    </div>
  );
};

type DataTableProps = {
  data: IModelExtended[];
  setSelectedData: Dispatch<SetStateAction<IModelExtended[]>>;
  modelType: string;
  isSortLoading?: boolean;
  noDataMessage?: string;
  emptyState?: React.ReactNode;
  sorting: SortingState;
  setSorting: Dispatch<SetStateAction<SortingState>>;
  isSortable?: boolean;
  isLoading?: boolean;
  isReducedColumns?: boolean;
  isCompact?: boolean;
  maxModels?: number;
  cantSelectMore?: boolean;
  /** Provider TYPE → logo lookup used by the compact model cell as a
   *  fallback when ``ModelDefinition.provider_image`` isn't set. Keyed
   *  on the no-llm protocol so per-tenant renames don't break it. */
  providerLogoByType?: Record<string, string>;
  className?: string;
};

export function ModelPickerTable({
  data,
  setSelectedData,
  modelType,
  noDataMessage = "No models to show",
  emptyState,
  sorting,
  setSorting,
  isSortLoading,
  isSortable = false,
  isLoading = false,
  isReducedColumns = false,
  isCompact = false,
  maxModels,
  cantSelectMore,
  providerLogoByType,
  className,
}: DataTableProps) {
  const handleRowClick = (model: IModelExtended) => {
    if (isSortable) {
      if (
        cantSelectMore ||
        model.is_available === false ||
        model.is_active === false ||
        model.provider_is_active === false
      )
        return;
      setSelectedData((oldData) => [...oldData, model]);
    } else {
      setSelectedData((oldData) => [
        ...oldData.filter((oldModel) => oldModel.id !== model.id),
      ]);
    }
  };

  /* Columns */
  const columnHelper = createColumnHelper<any>();
  const modelLength = data.length;

  const titleByName = useMemo(() => {
    const map: Record<string, string> = {};
    for (const m of data) map[m.name] = m.title;
    return map;
  }, [data]);

  const columns: ColumnDef<any, any>[] = useMemo(() => {
    let cols = [
      {
        id: "select-col",
        cell: ({ row }: { row: Row<any> }) => (
          <div className="flex items-center justify-around">
            {!isSortable && (
              <span className="line-clamp-1">{row.index + 1}</span>
            )}
            <Checkbox
              className="ml-3"
              checked={!isSortable}
              disabled={cantSelectMore}
            />
          </div>
        ),
        meta: {
          width: "5%",
          minW: "5rem",
        },
      },
      columnHelper.accessor("model", {
        cell: (info) => {
          const m = info.row.original;
          // Compact mode drops the standalone Provider column and shows
          // the provider inline after the model name ("Name – ◇ Provider").
          // Reuse the same provider-logo resolution as the Provider cell.
          const fallbackLogo =
            providerLogoByType &&
            (m as { provider_type?: string }).provider_type
              ? providerLogoByType[
                  (m as { provider_type?: string }).provider_type as string
                ]
              : undefined;
          const providerLogo = m.provider_image ?? fallbackLogo;
          const isProviderDead =
            (m as { provider_is_active?: boolean }).provider_is_active ===
            false;
          const isLinkDisabled = m?.is_active === false;
          const isUnavailable = isProviderDead || isLinkDisabled;
          const unavailableTooltip = isLinkDisabled
            ? "Model disabled: enable it in Settings → Models"
            : "Provider disconnected: enable it in Settings → Providers";
          return (
            <div
              className={cn(
                "flex items-center justify-start gap-2 min-w-0",
                isUnavailable && "opacity-60"
              )}
            >
              <div className="relative w-4 h-4 shrink-0">
                <ImageFallback
                  fill
                  src={m.image || "/images/placeholder.svg"}
                  alt={`Logo for ${m.label}`}
                  className="dark:invert dark:brightness-0"
                />
              </div>
              <span className="truncate text-primary">{m.title}</span>
              <RetirementNotice
                retirementDate={m.retirement_date}
                replacedBy={
                  m.replaced_by
                    ? (titleByName[m.replaced_by] ?? m.replaced_by)
                    : null
                }
              />
              {isCompact && m.provider && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-muted text-[11px] text-muted-foreground shrink-0 max-w-[120px] truncate">
                  {m.provider}
                </span>
              )}
              {(() => {
                if (isUnavailable)
                  return (
                    <NoxusTooltip content={unavailableTooltip} placement="top">
                      <span
                        className="inline-flex"
                        data-suppress-row-tooltip="true"
                      >
                        <Link2Off
                          size="1rem"
                          className="shrink-0 text-muted-foreground"
                          aria-label="Provider disconnected"
                        />
                      </span>
                    </NoxusTooltip>
                  );
                const health = m as unknown as ModelHealthFields | undefined;
                const ls = health?.lifecycle_state;
                const statusMap: Record<string, ModelStatus> = {
                  degraded: "degraded",
                  suspended: "suspended",
                  auto_deactivated: "auto_deactivated",
                  manually_deactivated: "deactivated",
                };
                if (ls && statusMap[ls])
                  return (
                    <ModelStatusBadge
                      status={statusMap[ls]}
                      lastError={health?.last_error}
                      consecutiveFailures={health?.consecutive_failures}
                      lastCheckedAt={health?.last_checked_at}
                      iconOnly
                    />
                  );
                return null;
              })()}
              {m?.privacy_level === "full" && (
                <ShieldCheck
                  size="0.75rem"
                  strokeWidth="1.25px"
                  className="text-muted-foreground"
                />
              )}
            </div>
          );
        },
        header: "Model",
        meta: {
          isSortable: false,
          // Compact mode: Model column absorbs the provider inline, so
          // give it most of the width. Non-compact keeps the original
          // 25% (Provider has its own column there).
          width: isCompact ? "70%" : "25%",
        },
      }),
      columnHelper.accessor("provider", {
        cell: (info) => {
          const m = info.row.original;
          // Per-link rows carry ``provider_image`` from the matching
          // spotflow ProviderDefinition (keyed on no-llm type, so it
          // survives per-tenant renames). The type-keyed lookup is the
          // legacy fallback for rows that pre-date per-link emission.
          const fallbackLogo =
            providerLogoByType &&
            (m as { provider_type?: string }).provider_type
              ? providerLogoByType[
                  (m as { provider_type?: string }).provider_type as string
                ]
              : undefined;
          const providerLogo = m.provider_image ?? fallbackLogo;
          if (!m.provider) return null;
          return (
            <Badge
              variant="outline"
              className="gap-1 px-1.5 py-0.5 max-w-[140px]"
            >
              {providerLogo && (
                <img
                  src={providerLogo}
                  alt=""
                  className="size-3 shrink-0 object-contain grayscale"
                  loading="lazy"
                  decoding="async"
                  draggable={false}
                />
              )}
              <span className="truncate">{info.getValue()}</span>
            </Badge>
          );
        },
        header: "Provider",
        meta: {
          // Drop sort arrows in compact so the column fits "Provider"
          // and longer names like "Google Vertex AI" without
          // truncating. Fixed rem (not a percentage) keeps the column
          // the same width regardless of which pane it's in.
          isSortable: isSortable && !isCompact,
          width: isCompact ? "11rem" : "8%",
        },
      }),
      columnHelper.accessor("host", {
        cell: (info) => <span className="line-clamp-1">{info.getValue()}</span>,
        header: "Host",
        meta: {
          isSortable: isSortable,
          width: "13%",
        },
      }),
      columnHelper.accessor("normalizedSpeed", {
        cell: (info) => getDonutIcon(info.getValue()),
        header: "Speed",
        meta: {
          isSortable: isSortable,
          // Compact: snug width that exactly fits "Speed" + sort
          // arrows — no spare room. Sits at the right of the row next
          // to Quality.
          width: isCompact ? "6rem" : "10%",
          align: "center",
        },
      }),
      columnHelper.accessor("quality", {
        cell: (info) => getDonutIcon(info.getValue()),
        header: "Quality",
        meta: {
          isSortable: isSortable,
          // Compact: snug width for the "Quality" label + sort arrows.
          width: isCompact ? "6rem" : "10%",
          align: "center",
        },
      }),
      columnHelper.accessor("location", {
        cell: (info) => getLocationImage(info.getValue()),
        header: "Location",
        meta: {
          isSortable: isSortable,
          width: "12%",
        },
      }),
      {
        id: "drag-col",
        cell: () => (
          <div className="flex justify-end">
            {isSortable || modelLength === 1 ? (
              <div className="grow" />
            ) : (
              <RowDragHandleCell modelLen={modelLength} />
            )}
          </div>
        ),
        meta: {
          // Compact: cell carries px-4 (32px horizontal padding) and
          // the <button> wrapping the grip has its own user-agent
          // padding, so 3rem squeezed the icon out under
          // overflow-x-hidden. 3.5rem leaves room for both.
          width: isCompact ? "3.5rem" : "5%",
        },
      },
    ];

    if (isCompact) {
      // Compact mode: Model (with provider inline) + Speed + Quality
      // gauges. Drag column comes back on the selected list
      // (isSortable=false) so users can reorder picks.
      cols = cols.filter((col) => {
        const id =
          "id" in col
            ? col.id
            : "accessorKey" in col
              ? col.accessorKey
              : undefined;
        const header = "header" in col ? col.header : undefined;

        // Allow drag-col if !isSortable (meaning it's the selected list)
        if (id === "drag-col" && !isSortable) return true;

        return header === "Model" || header === "Speed" || header === "Quality";
      });
    } else if (isReducedColumns) {
      cols = [...cols.slice(0, 4), ...cols.slice(6)];
    }

    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cantSelectMore, isCompact, providerLogoByType]);

  /* Table setup */
  const table = useReactTable({
    columns,
    data,
    state: {
      sorting,
    },
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    manualSorting: true,
    getRowId: (row) => row.id,
  });

  // Drag ordering related functions
  const [isAnyDragging, setIsAnyDragging] = useState(false);
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  function handleDragEnd(event: DragEndEvent) {
    setIsAnyDragging(false);
    const { active, over } = event;

    if (setSelectedData && active && over && active.id !== over.id) {
      setSelectedData((data) => {
        const oldIndex = data.findIndex((item) => item.id === active.id);
        const newIndex = data.findIndex((item) => item.id === over.id);

        return arrayMove(data, oldIndex, newIndex);
      });
    }
  }

  const colgroup = (
    <colgroup>
      {table.getAllColumns().map((col) => {
        const meta: any = col.columnDef.meta;
        return (
          <col
            key={col.id}
            style={{ width: meta?.width, minWidth: meta?.minW }}
          />
        );
      })}
    </colgroup>
  );

  const headerRows = table.getHeaderGroups().map((headerGroup) => (
    <tr key={headerGroup.id} className="border-b">
      {headerGroup.headers.map((header) => {
        const meta: any = header.column.columnDef.meta;
        const sort = header.column.getIsSorted();

        return (
          <TableHead
            key={header.id}
            onClick={
              meta?.isSortable
                ? header.column.getToggleSortingHandler()
                : undefined
            }
            className={cn(
              "select-none py-3 px-4 h-auto",
              meta?.isSortable &&
                (isSortLoading ? "cursor-not-allowed" : "cursor-pointer")
            )}
          >
            <div
              className={cn(
                "flex items-center",
                meta?.align === "center" ? "justify-center" : ""
              )}
            >
              {typeof header.column.columnDef.header === "string" ? (
                <span className="normal-case text-sm font-medium overflow-hidden text-ellipsis whitespace-nowrap font-['Inter']">
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                </span>
              ) : (
                flexRender(header.column.columnDef.header, header.getContext())
              )}

              {meta?.isSortable && (
                <div className="flex flex-col ml-2">
                  <ChevronUp
                    aria-label="Sorted ascending"
                    className={
                      sort === "asc" ? "text-primary" : "text-muted-foreground"
                    }
                    size="0.5rem"
                  />

                  <ChevronDown
                    aria-label="Sorted descending"
                    className={
                      sort === "desc" ? "text-primary" : "text-muted-foreground"
                    }
                    size="0.5rem"
                  />
                </div>
              )}
            </div>
          </TableHead>
        );
      })}
    </tr>
  ));

  return (
    <div
      className={cn(
        "bg-background border border-border rounded-lg flex flex-col overflow-hidden",
        isAnyDragging && "[&_*]:!cursor-grabbing",
        className
      )}
    >
      <DndContext
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis, restrictToParentElement]}
        onDragStart={() => setIsAnyDragging(true)}
        onDragCancel={() => setIsAnyDragging(false)}
        onDragEnd={handleDragEnd}
        sensors={sensors}
      >
        {/* Fixed header */}
        <table className="w-full table-fixed text-sm shrink-0">
          {colgroup}
          <TableHeader className="w-full bg-background [&_tr]:border-b [&_tr]:hover:bg-transparent">
            {headerRows}
          </TableHeader>
        </table>

        {/* Scrollable body */}
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
          {isLoading ? (
            <table className="w-full table-fixed text-sm">
              {colgroup}
              <TableBody>
                <tr>
                  {[...Array(6)].map((_, index) => (
                    <TableCell key={`loading-cell-${index}`} className="p-2">
                      <div className="h-6 w-full">
                        <Skeleton className="h-full" />
                      </div>
                    </TableCell>
                  ))}
                </tr>
              </TableBody>
            </table>
          ) : (
            <table className="w-full table-fixed text-sm">
              {colgroup}
              <TableBody className="[&_tr:last-child]:!border-b">
                <SortableContext
                  items={data}
                  strategy={verticalListSortingStrategy}
                >
                  {table.getRowModel().rows.map((row) => (
                    <DraggableRow
                      key={row.id}
                      row={row}
                      modelType={modelType}
                      isReducedColumns={isReducedColumns}
                      handleRowClick={handleRowClick}
                      cantSelectMore={cantSelectMore}
                      isAnyDragging={isAnyDragging}
                      titleByName={titleByName}
                    />
                  ))}
                </SortableContext>
              </TableBody>
            </table>
          )}

          {(!data || data.length === 0) &&
            !isLoading &&
            (emptyState ?? (
              <div className="flex justify-center items-center w-full h-full p-4">
                <NoxusAlert look="banner" title={noDataMessage} />
              </div>
            ))}
        </div>
      </DndContext>
    </div>
  );
}

// Cell Component
// Drag listeners/attributes from the row's single `useSortable` call.
// We pass them down via Context (instead of calling useSortable again
// inside the handle) because dnd-kit registers a fresh draggable per
// hook call — duplicates with the same id silently break activation.
interface DragHandleCtx {
  attributes?: DraggableAttributes;
  listeners?: DraggableSyntheticListeners;
}
const DragHandleContext = createContext<DragHandleCtx>({});

const RowDragHandleCell = ({ modelLen }: { modelLen: number }) => {
  const { attributes, listeners } = useContext(DragHandleContext);
  return (
    <button
      {...attributes}
      {...(listeners ?? {})}
      data-testid="model-drag-handle"
      disabled={modelLen === 0}
      // Swallow click + mousedown so grabbing the handle doesn't bubble
      // to the row's onClick (which removes the model from the
      // selected list).
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      className={cn(modelLen > 0 ? "cursor-pointer" : "cursor-not-allowed")}
    >
      <GripVertical size="1rem" strokeWidth="1.25px" />
    </button>
  );
};

// Row Component
const DraggableRow = ({
  row,
  modelType,
  isReducedColumns,
  handleRowClick,
  cantSelectMore,
  isAnyDragging = false,
  titleByName,
}: {
  row: Row<any>;
  modelType: string;
  isReducedColumns: boolean;
  handleRowClick: (model: IModelExtended) => void;
  cantSelectMore?: boolean;
  isAnyDragging?: boolean;
  titleByName: Record<string, string>;
}) => {
  const hoverTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const {
    attributes,
    listeners,
    transform,
    transition,
    setNodeRef,
    isDragging,
  } = useSortable({
    id: row.original.id,
  });

  const { refs, floatingStyles } = useFloating({
    placement: "right-start",
    strategy: "fixed",
    middleware: [
      offset(10),
      shift({ padding: 8 }),
      flip({
        fallbackPlacements: ["right-end", "left-start", "bottom-start"],
      }),
    ],
  });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition: transition,
    opacity: !row.original.is_available ? 0.5 : isDragging ? 0.8 : 1,
    zIndex: isDragging ? 1 : 0,
    position: "relative",
  };

  const isOverSuppressedChild = (target: EventTarget | null): boolean => {
    const el = target as HTMLElement | null;
    return !!el?.closest?.("[data-suppress-row-tooltip]");
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    // Children that own their own hover surface (status icon's hover
    // card today) opt out via `data-suppress-row-tooltip`. When the
    // cursor is on one, kill any pending row tooltip and close it if
    // already open so the two cards never stack.
    if (isOverSuppressedChild(e.target)) {
      if (hoverTimeout.current) {
        clearTimeout(hoverTimeout.current);
        hoverTimeout.current = null;
      }
      if (isOpen) setIsOpen(false);
      return;
    }
    const virtualRef = {
      getBoundingClientRect: () => ({
        width: 0,
        height: 0,
        x: e.clientX,
        y: e.clientY,
        top: e.clientY,
        left: e.clientX,
        right: e.clientX,
        bottom: e.clientY,
      }),
    };
    refs.setPositionReference(virtualRef);
  };

  const handleMouseEnter = (e: React.MouseEvent) => {
    if (isOverSuppressedChild(e.target)) return;
    handleMouseMove(e);

    hoverTimeout.current = setTimeout(() => {
      if (isReducedColumns || isAnyDragging) return;
      setIsOpen(true);
    }, 500);
  };

  const handleMouseLeave = () => {
    if (hoverTimeout.current) {
      clearTimeout(hoverTimeout.current);
    }
    if (isOpen) {
      setIsOpen(false);
    }
  };

  return (
    <>
      {isOpen && !isAnyDragging && (
        <Portal>
          <div
            ref={refs.setFloating}
            className="pointer-events-none z-9999"
            style={floatingStyles}
          >
            {row.original.is_available ? (
              <ModelTooltip
                model={row.original}
                modelType={modelType}
                replacedBy={
                  row.original.replaced_by
                    ? (titleByName[row.original.replaced_by] ??
                      row.original.replaced_by)
                    : null
                }
              />
            ) : (
              <MessageTooltip message="Contact an admin to enable this model." />
            )}
          </div>
        </Portal>
      )}
      <DragHandleContext.Provider value={{ attributes, listeners }}>
        <tr
          ref={setNodeRef}
          className={cn(
            "border-b transition-colors",
            cantSelectMore
              ? "cursor-not-allowed"
              : "cursor-pointer hover:bg-accent/50"
          )}
          style={style}
          onMouseMove={handleMouseMove}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onMouseDown={handleMouseLeave}
          onClick={() => handleRowClick(row.original)}
        >
          {row.getVisibleCells().map((cell, index) => (
            <TableCell
              key={cell.id}
              style={{ width: cell.column.getSize() }}
              className={cn(
                "text-sm py-3 px-4",
                index === 1 ? "" : "text-muted-foreground"
              )}
            >
              <div
                className={cn(
                  "flex items-center",
                  (cell.column.columnDef.meta as any)?.align === "center"
                    ? "justify-center"
                    : ""
                )}
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </div>
            </TableCell>
          ))}
        </tr>
      </DragHandleContext.Provider>
    </>
  );
};

const MessageTooltip = ({ message }: { message: string }) => {
  return (
    <div className="bg-background rounded-lg shadow-lg p-2 px-4" role="tooltip">
      <p className="text-xs">{message}</p>
    </div>
  );
};

// Tooltip
const ModelTooltip = ({
  model,
  modelType,
  replacedBy,
}: {
  model: IModelExtended;
  modelType: string;
  replacedBy: string | null;
}) => {
  const tooltipData = useMemo(() => {
    const timeOptions: Intl.DateTimeFormatOptions = {
      day: "numeric",
      month: "short",
      year: "numeric",
    };

    const info = [
      {
        title: "Speed:",
        value: `${model.speed || "-"} ${modelType === "text" ? "token" : "image"}${model.speed === 1 ? "" : "s"} per second`,
      },
      {
        title: "Quality:",
        value: model.quality || "-",
      },
      {
        title: "Max input tokens:",
        value:
          modelType === "text"
            ? `${formatNumber((model.properties as LlmModelProperties).max_input_tokens) || "-"}`
            : "",
      },
      {
        title: "Release date:",
        value: `${formatTime(model.release_date || "", timeOptions) || "-"}`,
      },
      {
        title: "Data cutoff date:",
        value: `${formatTime(model.data_cutoff_date || "", timeOptions) || "-"}`,
      },
      {
        title: "Data privacy level:",
        value: capitalizeFirstLetter(model.privacy_level || "Basic"),
      },
    ];

    return modelType === "text"
      ? info
      : info.filter((_, index) => index != 2 && index != 5);
  }, [model, modelType]);

  const isDataSecure = model.privacy_level === "full";

  return (
    <div
      // Grow as needed up to max-w-md so longer titles like
      // "Gemini 3.1 Flash Image Preview" stop ellipsising.
      className="bg-background rounded-xl shadow-xl border border-border/60 relative min-w-72 max-w-md p-4"
      role="tooltip"
    >
      {!model ? (
        <p className="text-xs text-muted-foreground">
          No information available for this model
        </p>
      ) : (
        <>
          {/* Header */}
          <div className="flex items-center gap-3 pb-3 border-b border-border">
            <div className="relative w-8 h-8 shrink-0">
              <ImageWithFallback
                fill
                alt={model.title + " logo"}
                src={model.image || "/images/placeholder.svg"}
              />
            </div>
            <div className="flex-1 min-w-0 flex flex-col">
              <p className="text-sm font-semibold break-words flex items-center gap-1.5">
                {model.title}
                <RetirementNotice
                  retirementDate={model.retirement_date}
                  replacedBy={replacedBy}
                  size={14}
                />
              </p>
              {model.provider && (
                <p className="text-xs text-muted-foreground truncate">
                  {model.provider}
                </p>
              )}
            </div>
            {model.location && (
              <div className="w-5 h-5 relative shrink-0">
                {getLocationImage(model.location, true)}
              </div>
            )}
          </div>

          {/* Stats */}
          <div className="flex flex-col gap-1.5 py-3">
            {tooltipData.map((dataPoint, index) => (
              <div
                key={`model.id-${index}`}
                className="flex text-xs justify-between w-full gap-4"
              >
                <span className="text-muted-foreground">{dataPoint.title}</span>
                <span className="font-medium">{dataPoint.value}</span>
              </div>
            ))}
          </div>

          {/* Capabilities */}
          {model.capabilities && model.capabilities.length > 0 && (
            <div className="border-t border-border pt-3">
              <p className="text-xs font-medium text-muted-foreground mb-2">
                Capabilities
              </p>
              <div className="flex flex-wrap gap-1.5">
                {sortCapabilities(model.capabilities).map((cap) => (
                  <CapabilityChip key={cap} cap={cap} />
                ))}
              </div>
            </div>
          )}

          {/* Data secure */}
          {isDataSecure && (
            <>
              <Separator className="mt-3 mb-2" />
              <div className="flex justify-center items-center gap-1.5">
                <ShieldCheck
                  size="0.875rem"
                  strokeWidth="1.25px"
                  className="text-emerald-500"
                />
                <span className="text-xs font-medium">Data secure</span>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};
